// Save error diagnostics and retry policy for the autosave path.
// Pure functions — no side effects, no React imports.

/**
 * Returns a user-visible save-failure message based on the server's error code
 * and HTTP status.  Messages deliberately avoid exposing backend internals.
 *
 * All messages emphasize that the data is stored on the device but NOT yet
 * synced to the cloud, so the user understands the persistence state clearly.
 */
export function buildSaveErrorMsg(code, status) {
  switch (code) {
    case 'SECURITY_STATE_UNAVAILABLE':
      return '⚠ Cloud save temporarily unavailable — changes are stored on this device and are not yet synced to cloud';
    case 'CROSS_TENANT':
    case 'FORBIDDEN':
      return '⚠ Cloud save blocked — changes on this device are not yet synced to cloud';
    case 'DATA_INTEGRITY':
      return '⚠ Cloud save blocked by an account configuration error — changes on this device are not yet synced to cloud';
    default:
      if (status === 401) return '⚠ Session expired — sign in again. Changes are stored on this device and are not yet synced.';
      return '⚠ Cloud save failed — changes are stored on this device and are not yet synced to cloud';
  }
}

/**
 * Returns true only for error codes that are safe to retry automatically.
 *
 * SECURITY_STATE_UNAVAILABLE: a transient Redis read failure — the enforcement
 * flag is simply temporarily unreadable.  A short retry is safe because retrying
 * does not weaken tenant isolation; it either reads the flag successfully or
 * fails closed again.
 *
 * NOT retried (explicit exclusions):
 *   401 — session expired; a retry with the same token will also fail
 *   403 FORBIDDEN / CROSS_TENANT — a policy rejection; retrying cannot change it
 *   503 DATA_INTEGRITY — a persistent data-model issue; retrying cannot fix it
 *   409 — revision conflict; handled separately by the conflict flow
 */
export function isSaveRetryable(code) {
  return code === 'SECURITY_STATE_UNAVAILABLE';
}
